import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "./ScrollEffects";
import { WordReveal } from "./TextEffects";

interface FAQItem {
  q: string;
  a: string;
}

function FAQItemComponent({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ delay: index * 0.06, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
      className="border-b border-border/50 last:border-0"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="font-bold text-foreground text-base pr-4 group-hover:text-primary transition-colors duration-300">
          {q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3, type: "spring", stiffness: 300 }}
          className="text-xl text-muted-foreground flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center"
        >
          +
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="overflow-hidden"
          >
            <p className="text-muted-foreground text-sm leading-relaxed pb-5 pl-1">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export const FAQSection: React.FC = () => {
  const { t } = useTranslation();
  const faqs = t('faq.items', { returnObjects: true }) as FAQItem[];

  return (
    <section className="py-24 sm:py-32 px-4 relative" id="faq">
      <div className="absolute inset-0 bg-card/40 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="max-w-3xl mx-auto relative z-10">
        <ScrollReveal className="text-center mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-4">
            {t('faq.section_badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground mb-4">
            <WordReveal
              text={`${t('faq.section_title_prefix')} ${t('faq.section_title_highlight')}`}
              highlightWords={t('faq.section_title_highlight').split(' ')}
            />
          </h2>
        </ScrollReveal>

        <div className="glass rounded-3xl border border-border/50 px-6 sm:px-8 shadow-warm-sm">
          {faqs.map((faq, i) => (
            <FAQItemComponent key={i} q={faq.q} a={faq.a} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
